import { useAuth } from "@/lib/auth-context";
import { useSyncProgress } from "@/lib/use-sync-progress";
import { cn } from "@/lib/utils";
import {
	Activity,
	Building2,
	CalendarDays,
	LayoutDashboard,
	LogOut,
	Mail,
	Menu,
	Receipt,
	RefreshCw,
	Search,
	Settings,
	Square,
	Users,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

// ============================================================
// Layout — Sidebar + Header + Content
// ============================================================

const NAV_ITEMS = [
	{ to: "/", icon: LayoutDashboard, label: "Dashboard" },
	{ to: "/bookings", icon: CalendarDays, label: "Buchungen" },
	{ to: "/contacts", icon: Users, label: "Kontakte" },
	{ to: "/companies", icon: Building2, label: "Firmen" },
	{ to: "/emails", icon: Mail, label: "E-Mails" },
	{ to: "/lexoffice", icon: Receipt, label: "LexOffice" },
	{ to: "/health", icon: Activity, label: "System" },
	{ to: "/settings", icon: Settings, label: "Einstellungen" },
] as const;

export function Layout() {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [search, setSearch] = useState("");
	const { isRunning, percent, progress, abortMutation } = useSyncProgress();

	function handleSearch(e: React.FormEvent) {
		e.preventDefault();
		if (search.trim()) {
			navigate(`/bookings?q=${encodeURIComponent(search.trim())}`);
			setSearch("");
			setSidebarOpen(false);
		}
	}

	return (
		<div className="flex h-full">
			{/* Mobile Overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 z-40 bg-black/30 lg:hidden"
					role="button"
					tabIndex={0}
					onClick={() => setSidebarOpen(false)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") setSidebarOpen(false);
					}}
				/>
			)}

			{/* Sidebar */}
			<aside
				className={cn(
					"fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-stone-200 bg-white transition-transform lg:static lg:translate-x-0",
					sidebarOpen ? "translate-x-0" : "-translate-x-full",
				)}
			>
				{/* Logo */}
				<div className="flex h-16 items-center gap-3 border-b border-stone-100 px-5">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 font-display text-sm text-white">
						bh
					</div>
					<div>
						<div className="font-display text-lg leading-tight">
							Booking Hub
						</div>
						<div className="text-[10px] uppercase tracking-widest text-stone-400">
							as.if.spaces
						</div>
					</div>
				</div>

				{/* Navigation */}
				<nav className="flex-1 space-y-0.5 px-3 py-4">
					{NAV_ITEMS.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end={item.to === "/"}
							onClick={() => setSidebarOpen(false)}
							className={({ isActive }) =>
								cn(
									"flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
									isActive
										? "bg-brand-50 text-brand-700"
										: "text-stone-600 hover:bg-stone-50 hover:text-stone-900",
								)
							}
						>
							<item.icon className="h-4.5 w-4.5 shrink-0" size={18} />
							{item.label}
						</NavLink>
					))}
				</nav>

				{/* User */}
				<div className="border-t border-stone-100 p-3">
					<div className="flex items-center justify-between rounded-lg px-3 py-2">
						<div>
							<div className="text-sm font-medium text-stone-700">
								{user?.name}
							</div>
							<div className="text-xs text-stone-400">{user?.email}</div>
						</div>
						<button
							type="button"
							onClick={logout}
							className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
							title="Ausloggen"
						>
							<LogOut size={16} />
						</button>
					</div>
				</div>
			</aside>

			{/* Main */}
			<div className="flex flex-1 flex-col min-w-0">
				{/* Header */}
				<header className="flex h-16 items-center gap-4 border-b border-stone-200 bg-white px-4 lg:px-6">
					<button
						type="button"
						onClick={() => setSidebarOpen(true)}
						className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 lg:hidden"
					>
						<Menu size={20} />
					</button>

					{/* Search */}
					<form onSubmit={handleSearch} className="relative flex-1 max-w-md">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Buchung suchen..."
							className="input pl-9"
						/>
					</form>
				</header>

				{/* Sync Progress Bar */}
				{isRunning && (
					<div className="border-b border-brand-100 bg-brand-50/60 px-4 lg:px-6 py-1.5">
						<div className="flex items-center gap-2 text-xs text-brand-700">
							<RefreshCw size={12} className="animate-spin shrink-0" />
							<span className="font-medium">
								LexOffice Sync
								{progress?.total
									? ` — ${progress.processed}/${progress.total}`
									: ""}
							</span>
							{progress?.total ? (
								<div className="flex-1 max-w-xs h-1.5 bg-brand-100 rounded-full overflow-hidden">
									<div
										className="h-full bg-brand-500 rounded-full transition-all duration-500"
										style={{ width: `${percent}%` }}
									/>
								</div>
							) : null}
							<span className="text-brand-400">{percent}%</span>
							<button
								type="button"
								onClick={() => abortMutation.mutate()}
								disabled={abortMutation.isPending}
								className="ml-1 rounded p-0.5 text-brand-400 hover:bg-brand-100 hover:text-brand-700 transition-colors"
								title="Sync abbrechen"
							>
								<Square size={10} className="fill-current" />
							</button>
						</div>
					</div>
				)}

				{/* Content */}
				<main className="flex-1 overflow-y-auto p-4 lg:p-6">
					<Outlet />
				</main>
			</div>
		</div>
	);
}
